import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type PrescriptionQuote } from '../api';

/**
 * Which pharmacies can actually fill this prescription, and roughly for how
 * much.
 *
 * The pharmacy page has been promising "compare pharmacy prices" above a list
 * of shops with no prices on it. `GET /pharmacy/quotes` is what makes that
 * true: the server reads the prescription, matches each medicine against
 * every public catalogue by name and then by generic, and reports what it
 * could not find.
 *
 * Two things are said plainly because the estimate is easy to over-read. It
 * prices one unit of each medicine — the quantity is settled in the cart — and
 * a shop that stocks only part of the prescription is shown as partial with
 * the missing medicines named, never ranked on a cheaper, shorter list.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
  padding: 14, fontFamily: T.font,
};

export default function PrescriptionQuotes({ prescriptionId }: { prescriptionId: string }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState<PrescriptionQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setQuote(await patientApi.pharmacyModule.quote(prescriptionId));
    } catch (e: any) {
      setError(e?.body?.message || 'Prices for this prescription could not be worked out right now.');
    } finally {
      setLoading(false);
    }
  }, [prescriptionId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>
        Asking the pharmacies what this prescription costs…
      </div>
    );
  }

  if (error) {
    return <div style={{ ...card, borderColor: `${T.danger}55`, fontSize: 11.5, color: T.danger }}>{error}</div>;
  }

  if (!quote) return null;

  const { medicines, stores } = quote;

  if (medicines.length === 0) {
    return (
      <div style={{ ...card, fontSize: 11.5, color: T.textSec, lineHeight: 1.6 }}>
        This prescription lists no medicines to price.
      </div>
    );
  }

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: 14, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>
          What this prescription costs, shop by shop
        </div>
        <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 4, lineHeight: 1.5 }}>
          {medicines.map((m) => [m.name, m.strength].filter(Boolean).join(' ')).join(' · ')}
          {quote.doctorName ? ` — prescribed by ${quote.doctorName}` : ''}
        </div>
      </div>

      {stores.length === 0 ? (
        <div style={{ padding: 14, fontSize: 11.5, color: T.textSec, lineHeight: 1.6 }}>
          No pharmacy on the network has an online catalogue yet, so there is nothing to price this against.
          You can still take the prescription to any of the shops below.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {stores.map((s) => (
            <div key={s.slug} style={{ padding: 14, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>
                    {s.name}
                    {s.verified && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700, color: T.white,
                        background: T.teal, borderRadius: 6, padding: '2px 6px', verticalAlign: 'middle',
                      }}>
                        Verified
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>{s.city ?? ''}</div>

                  <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
                    {s.matched.map((m) => (
                      <div key={m.name}>
                        {m.matchedAs} · ₹{m.price.toLocaleString('en-IN')}
                        {/* Stock is the shop's own count; out of stock still
                            appears, because the price is still the answer to
                            "what would this cost here". */}
                        {!m.inStock && <span style={{ color: T.danger }}> · out of stock</span>}
                      </div>
                    ))}
                  </div>

                  {s.missing.length > 0 && (
                    <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 6, lineHeight: 1.5 }}>
                      Does not list {s.missing.join(', ')} — the estimate below leaves
                      {s.missing.length === 1 ? ' it' : ' them'} out.
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {/* A shop that stocks none of it has no price, and ₹0 would
                      read as free. It says what it actually means instead. */}
                  <div style={{ fontSize: 14, fontWeight: 800, color: s.matched.length > 0 ? T.navy : T.textTer }}>
                    {s.matched.length > 0 ? `₹${s.estimateTotal.toLocaleString('en-IN')}` : 'No price'}
                  </div>
                  <div style={{ fontSize: 9, color: s.complete ? T.teal : T.textTer, fontWeight: 700, marginTop: 2 }}>
                    {s.complete ? 'Has everything' : (s.matched.length === 0 ? 'Stocks none of it' : `${s.matched.length} of ${medicines.length}`)}
                  </div>
                  <button
                    onClick={() => navigate(`/patient/care-services/pharmacy/${s.slug}?prescriptionId=${encodeURIComponent(prescriptionId)}`)}
                    style={{
                      marginTop: 8, padding: '7px 12px', borderRadius: 9, border: 'none', cursor: 'pointer',
                      fontSize: 10.5, fontWeight: 700, color: T.white, background: T.teal,
                      fontFamily: T.font, whiteSpace: 'nowrap',
                    }}
                  >
                    Open store
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: 12, fontSize: 10, color: T.textTer, lineHeight: 1.5 }}>
        One unit of each medicine, at the price the shop publishes today. The quantity, delivery and any
        substitution are settled in the shop's own cart.
      </div>
    </div>
  );
}
